import React from "react";

import { useQuery, useSubscription } from "@apollo/client";
import { remove } from "lodash";
import { useNavigate } from "react-router-dom";

import { graphql } from "@/gql";
import { MessageListItemFragment } from "@/gql/graphql";
import { extractStoreFieldArgs } from "@/utils/apollo";

const coachSuspendedQuery = graphql(`
  query CoachSuspended {
    coachViewer {
      id
      subscriptionSuspended
    }
  }
`);

const messageSentSubscription = graphql(`
  subscription MessageSent {
    messageSent {
      message {
        ...ChatMessage
      }
    }
  }
`);

const messageReadSubscription = graphql(`
  subscription MessageRead {
    messageRead {
      message {
        id
        readAt
      }
    }
  }
`);

const GlobalListeners: React.FC = ({ children }) => {
  const navigate = useNavigate();

  useQuery(coachSuspendedQuery, {
    onCompleted: (data) => {
      if (data.coachViewer.subscriptionSuspended) {
        navigate("/payment-failed", { replace: true });
      }
    },
  });

  useSubscription(messageSentSubscription, {
    fetchPolicy: "network-only",
    onData: ({ client, data }) => {
      const message = data.data?.messageSent?.message;
      if (message == null) return;

      client.cache.modify({
        id: `Chat:${message.chatId}`,
        fields: {
          latestMessage: (_, { toReference }) => {
            return toReference(message);
          },
          messages: (previous, { toReference, fieldName }) => {
            const args = extractStoreFieldArgs(fieldName);
            if (args.channel !== message.channel) return;
            return {
              ...previous,
              edges: [
                { __typename: "MessageEdge", node: toReference(message) },
                ...previous.edges,
              ],
            };
          },
          viewerUnreadMessagesCount: (previous, { storeFieldName }) => {
            if (previous == null) return previous;

            const args = extractStoreFieldArgs(storeFieldName);
            if (args.channel != null && args.channel !== message.channel) {
              return previous;
            }

            return previous + 1;
          },
        },
      });

      //  Put chat to top of chat list
      client.cache.modify({
        fields: {
          chats: (previous, { storeFieldName }) => {
            const args = extractStoreFieldArgs(storeFieldName);
            if (args.participantName !== "") return;

            const nodes = [...previous.nodes];

            const [chat] = remove(
              nodes,
              (n) => n.__ref === `Chat:${message.chatId}`
            );
            if (chat) {
              return {
                ...previous,
                nodes: [chat, ...nodes],
              };
            } else {
              // This chat is not in cache?! How?! New client and message list
              // is not refreshed! For this, we try to make a chat fragment
              // and put it in top of chat list.
              const newChat: MessageListItemFragment = {
                __typename: "Chat",
                id: message.chatId,
                client: {
                  __typename: "Client",
                  id: message.sender.id,
                  profile: message.sender.profile,
                },
                viewerUnreadMessagesCount: 1,
                latestMessage: message,
              };
              // Store this fragment in the cache.
              client.cache.modify({
                fields: { [`Chat:${message.chatId}`]: () => newChat },
              });
              // Then put the reference of that cache to the list
              return {
                ...previous,
                nodes: [newChat, ...nodes],
              };
            }
          },
        },
      });
    },
  });

  useSubscription(messageReadSubscription, {
    fetchPolicy: "network-only",
  });

  return <>{children}</>;
};

export default GlobalListeners;
